import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  GlobalStyles,
} from '@mui/material';
import { Check, InfoOutlined, FileDownload } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import trimCanvas from 'trim-canvas';
import { useTranslation } from '@/core/i18n/i18n';
import { useAnyLoading } from '@/core/useAnyLoading';
import { setSnackAlert } from '@/core/slices/alert';
import DataSourceService from '@/core/services/dataSource';
import Navigation from '../Shared/Navigation.tsx';
import LightTooltip from '../Shared/LightTooltip';
import { isSelfAssessmentResultsStepPaused } from './resultsStepAvailability';
import PlanAnualIcon from '@/static/icons/Plan-anual.svg?url';
import {
  PlanValidation,
  SelfAssessmentResultsPayload,
  SelfAssessmentSummaryCard,
} from '@shared/schema';

interface CategoryData {
  categoryName: string;
  checklists: { ChecklistId: number; Name: string; progress: number }[];
  minScore: number;
  maxScore: number;
  avgScore: number;
}

const formatPercentage = (value: number | null | undefined, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Number(num.toFixed(digits));
  const safe = rounded === 0 ? 0 : rounded;
  const str = safe.toFixed(digits);
  return str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

const formatSignedPercentage = (value: number | null | undefined, digits = 1) => {
  const formatted = formatPercentage(value, digits);
  if (formatted === '0') return '0';
  return formatted.startsWith('-') ? formatted : `-${formatted}`;
};

const extractLevelNumber = (label: string | null | undefined) => {
  if (!label) return null;
  const match = String(label).match(/(\d+)/);
  return match ? Number(match[1]) : null;
};

interface SelfAssessmentResultsProps {
  currentFulfillmentVersion: number | null;
  fetchSelfAssessmentResults: (fulfillmentVersionId: number) => void;
  selectedYear: number | null;
  scopeName: string;
  enterpriseName?: string;
  currentPlanId?: number | null;
  currentPlanName?: string | null;
  currentBusinessUnit?: string | null;
  getPlanValidation: (
    fulfillmentVersionId: number,
  ) => Promise<PlanValidation | null | undefined> | PlanValidation | null | undefined;
  planValidation?: PlanValidation | null;
  results: SelfAssessmentResultsPayload | null;
  loadingResults: boolean;
}

const dataSource = DataSourceService({ baseUrl: '' });

const SelfAssessmentResults: React.FC<SelfAssessmentResultsProps> = ({
  currentFulfillmentVersion,
  fetchSelfAssessmentResults,
  selectedYear,
  scopeName,
  enterpriseName: _enterpriseName,
  currentPlanId: _currentPlanId,
  currentPlanName: _currentPlanName,
  currentBusinessUnit,
  getPlanValidation,
  planValidation,
  results,
  loadingResults,
}) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const dispatch = useDispatch();
  const runThunk = <T,>(thunk: unknown): Promise<T> =>
    (dispatch as unknown as (action: unknown) => Promise<T>)(thunk);
  const isAnyLoading = useAnyLoading();
  const enterpriseName =
    _enterpriseName ||
    (language === 'br' ? 'Empresa' : language === 'en' ? 'Enterprise' : 'Empresa');
  const enterpriseContributionLabel = t('selfAssessmentResults.contributionUltramar', {
    enterpriseName,
  });
  const businessLineContributionLabel = t('selfAssessmentResults.contributionBusinessLine');
  const enterpriseDiscountLabel = t('selfAssessmentResults.enterpriseDiscount', { enterpriseName });
  const businessLineDiscountLabel = t('selfAssessmentResults.businessLineDiscount');
  const top80ContributionYes = t('selfAssessmentResults.top80ContributionYes');
  const top80ContributionNo = t('selfAssessmentResults.top80ContributionNo');
  const [isDownloading, setIsDownloading] = useState(false);
  const [powerBiImage, setPowerBiImage] = useState<string | null>(null);
  const [powerBiNode0Image, setPowerBiNode0Image] = useState<string | null>(null);
  const [powerBiLoaded, setPowerBiLoaded] = useState(false);
  const [powerBiImageSizes, setPowerBiImageSizes] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const resultsStepPaused = isSelfAssessmentResultsStepPaused(selectedYear);

  useEffect(() => {
    if (resultsStepPaused) {
      navigate('/');
      return;
    }
    if (currentFulfillmentVersion) {
      fetchSelfAssessmentResults(currentFulfillmentVersion);
      getPlanValidation(currentFulfillmentVersion);
    }
  }, [
    currentFulfillmentVersion,
    fetchSelfAssessmentResults,
    getPlanValidation,
    navigate,
    resultsStepPaused,
  ]);

  useEffect(() => {
    setPowerBiImage(null);
    setPowerBiNode0Image(null);
    setPowerBiLoaded(false);
    setPowerBiImageSizes({});
  }, [currentFulfillmentVersion]);

  useEffect(() => {
    if (
      !resultsStepPaused &&
      planValidation?.APFulfillmentsVersionId === currentFulfillmentVersion &&
      planValidation.Approved === false
    ) {
      navigate('/ceo-validation');
    }
  }, [currentFulfillmentVersion, navigate, planValidation, resultsStepPaused]);

  const fallbackScope = useMemo(() => {
    const scopeValue = (results?.scopeName || scopeName || '').toLowerCase();
    return {
      isSecurityScope: scopeValue.includes('seguridad'),
      isSocioAmbientalScope: scopeValue.includes('socio') || scopeValue.includes('ambient'),
    };
  }, [results, scopeName]);

  const isSecurityScope = results?.isSecurityScope ?? fallbackScope.isSecurityScope;
  const isSocioAmbientalScope =
    results?.isSocioAmbientalScope ?? fallbackScope.isSocioAmbientalScope;
  const showCulture = isSecurityScope || isSocioAmbientalScope;

  const fallbackCutoff = useMemo(() => {
    const now = new Date();
    const planYear = results?.year ?? selectedYear ?? now.getFullYear();
    const candidateDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const cappedDate =
      candidateDate.getFullYear() > planYear
        ? new Date(planYear, 11, 1)
        : candidateDate.getFullYear() < planYear
          ? new Date(planYear, 0, 1)
          : candidateDate;
    const locale = language === 'es' ? 'es-CL' : language === 'br' ? 'pt-BR' : 'en-US';
    const monthName = cappedDate.toLocaleString(locale, { month: 'long' });
    const monthLabel = monthName ? monthName.charAt(0).toUpperCase() + monthName.slice(1) : '';
    return { monthLabel, year: cappedDate.getFullYear() };
  }, [language, results?.year, selectedYear]);

  const ytdCutoff = results?.ytdCutoff ?? fallbackCutoff;

  const summaryCards = results?.summary.cards ?? [];
  const overallAverage = results?.summary.overallAverage ?? 0;
  const overallWithoutAdjustments = results?.summary.overallWithoutAdjustments ?? null;
  const contributionChips = results?.summary.contribution ?? null;
  const auditPercentage = results?.auditPercentage ?? null;
  const managementFocusOriginal = results?.managementFocusOriginal ?? null;
  const workPlanWithDiscounts = summaryCards.find((card) => card.key === 'workPlan')?.value ?? null;

  const summaryTitle = (key: SelfAssessmentSummaryCard['key']) => {
    switch (key) {
      case 'workPlan':
        return t('selfAssessmentResults.workPlan');
      case 'managementFocus':
        return t('selfAssessmentResults.managementFocus');
      case 'leadershipPlan':
        return t('selfAssessmentResults.leadershipPlan');
      case 'operationalImpact':
        return t('selfAssessmentResults.operationalImpact');
      default:
        return key;
    }
  };

  const renderedSummaryCards = useMemo(
    () =>
      summaryCards.map((card) => ({
        ...card,
        title: summaryTitle(card.key),
      })),
    [summaryCards, t],
  );

  const visibleSummaryCards = useMemo(
    () => renderedSummaryCards.filter((card) => !isSocioAmbientalScope || card.key !== 'workPlan'),
    [isSocioAmbientalScope, renderedSummaryCards],
  );

  const maturityProgress = useMemo(() => {
    const map: Record<number, number> = {};
    results?.maturity.levels.forEach(({ level, percentage }) => {
      map[level] = percentage;
    });
    return map;
  }, [results]);

  const maturityLevelLabel = results?.maturity.levelLabel ?? '';
  const previousMaturityLevel = results?.previousMaturityLevel ?? null;
  const previousMaturitySource = results?.previousMaturitySource ?? null;
  const cultureLevel = results?.cultureLevel ?? null;
  const cultureLevelLabel = cultureLevel
    ? t('selfAssessmentResults.maturityLevelShort', { level: cultureLevel })
    : '—';
  const hasLegalNonComplianceDegradation =
    cultureLevel != null &&
    extractLevelNumber(maturityLevelLabel) != null &&
    cultureLevel !== extractLevelNumber(maturityLevelLabel);
  const previousLevelValue = previousMaturityLevel ?? 1;
  const previousLevelLabel = t('selfAssessmentResults.maturityLevelShort', {
    level: previousLevelValue,
  });
  const previousLevelTooltip =
    previousMaturitySource === 'historical' || previousMaturitySource === 'override'
      ? t('selfAssessmentResults.previousMaturityTooltip', { level: previousLevelValue })
      : t('selfAssessmentResults.previousMaturityFallback');

  const processedChecklistData = useMemo(() => {
    if (!results?.checklistCategories) return {};
    return results.checklistCategories.reduce(
      (acc, category) => {
        acc[category.categoryKey] = {
          categoryName: category.categoryName,
          checklists: category.checklists.map((chk) => ({
            ChecklistId: chk.checklistId,
            Name: chk.name,
            progress: chk.progress,
          })),
          minScore: category.minScore,
          maxScore: category.maxScore,
          avgScore: category.avgScore,
        };
        return acc;
      },
      {} as Record<string, CategoryData>,
    );
  }, [results]);

  const focusAdherenceData = results?.focusAdherence.items ?? [];
  const totalFocusAdherenceWeighted = results?.focusAdherence.totalWeightedAdherence ?? null;
  const leadershipActivities = results?.leadershipActivities ?? null;
  const globalAdherence = results?.workPlan.globalAdherence ?? 0;
  const focuses = results?.workPlan.focuses ?? [];
  const yearLabel = results?.year ?? selectedYear;
  const scopeLabel = results?.scopeName ?? scopeName;
  const isLoading = isAnyLoading || loadingResults;
  const stepGridSize = { xs: 12, sm: 6, md: 3 };
  const resolvedNode0Image =
    powerBiNode0Image && powerBiNode0Image !== powerBiImage ? powerBiNode0Image : null;
  const printPowerBiImages = [resolvedNode0Image, powerBiImage].filter(
    (image): image is string => Boolean(image),
  );
  const hasPowerBiImages = Boolean(powerBiImage || resolvedNode0Image);

  const PRINT_PAGE_MARGIN_MM = 10;
  const PRINT_A4_WIDTH_MM = 210;
  const PRINT_A4_HEIGHT_MM = 297;
  const PRINT_PAGE_WIDTH_MM = PRINT_A4_WIDTH_MM - PRINT_PAGE_MARGIN_MM * 2;
  const PRINT_PAGE_HEIGHT_MM = PRINT_A4_HEIGHT_MM - PRINT_PAGE_MARGIN_MM * 2;

  const trimPowerBiImageDataUrl = async (dataUrl: string): Promise<string> => {
    if (!dataUrl || !dataUrl.startsWith('data:image')) return dataUrl;

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Power BI image failed to load for trimming'));
      image.src = dataUrl;
    });

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) return dataUrl;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;

    ctx.drawImage(img, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;

    const samplePixel = (x: number, y: number) => {
      const i = (y * width + x) * 4;
      return {
        r: pixels[i] ?? 255,
        g: pixels[i + 1] ?? 255,
        b: pixels[i + 2] ?? 255,
      };
    };

    const cornerSamples = [
      samplePixel(0, 0),
      samplePixel(Math.max(0, width - 1), 0),
      samplePixel(0, Math.max(0, height - 1)),
      samplePixel(Math.max(0, width - 1), Math.max(0, height - 1)),
    ];

    // Pick the corner color that best represents the repeated background tone.
    const background = cornerSamples.reduce(
      (best, current) => {
        const currentScore = cornerSamples.reduce(
          (score, other) =>
            score +
            Math.abs(current.r - other.r) +
            Math.abs(current.g - other.g) +
            Math.abs(current.b - other.b),
          0,
        );
        if (currentScore < best.score) {
          return { score: currentScore, color: current };
        }
        return best;
      },
      { score: Number.POSITIVE_INFINITY, color: cornerSamples[0] },
    ).color;

    const tolerance = 56;
    const lightToneThreshold = 232;
    const pixelCount = width * height;
    const isBackgroundCandidate = new Uint8Array(pixelCount);
    const isConnectedBackground = new Uint8Array(pixelCount);

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const flat = y * width + x;
        const i = flat * 4;
        const r = pixels[i] ?? 255;
        const g = pixels[i + 1] ?? 255;
        const b = pixels[i + 2] ?? 255;
        const dr = Math.abs(r - background.r);
        const dg = Math.abs(g - background.g);
        const db = Math.abs(b - background.b);
        const isNearBackground = dr + dg + db <= tolerance;
        const isLightNeutral =
          r >= lightToneThreshold && g >= lightToneThreshold && b >= lightToneThreshold;
        if (isNearBackground || isLightNeutral) {
          isBackgroundCandidate[flat] = 1;
        }
      }
    }

    const componentQueue: number[] = [];
    const tryEnqueue = (x: number, y: number) => {
      if (x < 0 || y < 0 || x >= width || y >= height) return;
      const flat = y * width + x;
      if (!isBackgroundCandidate[flat] || isConnectedBackground[flat]) return;
      isConnectedBackground[flat] = 1;
      queue.push(flat);
    };

    for (let x = 0; x < width; x += 1) {
      tryEnqueue(x, 0);
      tryEnqueue(x, height - 1);
    }
    for (let y = 0; y < height; y += 1) {
      tryEnqueue(0, y);
      tryEnqueue(width - 1, y);
    }

    for (let head = 0; head < queue.length; head += 1) {
      const flat = queue[head];
      const x = flat % width;
      const y = Math.floor(flat / width);
      tryEnqueue(x - 1, y);
      tryEnqueue(x + 1, y);
      tryEnqueue(x, y - 1);
      tryEnqueue(x, y + 1);
    }

    for (let flat = 0; flat < pixelCount; flat += 1) {
      if (!isConnectedBackground[flat]) continue;
      pixels[flat * 4 + 3] = 0;
    }

    ctx.putImageData(imageData, 0, 0);
    const trimmedGlobal = trimCanvas(canvas);
    if (!trimmedGlobal.width || !trimmedGlobal.height) return dataUrl;

    const opaqueMask = new Uint8Array(pixelCount);
    for (let flat = 0; flat < pixelCount; flat += 1) {
      const alpha = pixels[flat * 4 + 3] ?? 0;
      if (alpha > 0) opaqueMask[flat] = 1;
    }

    const visited = new Uint8Array(pixelCount);
    let bestSize = 0;
    let bestMinX = 0;
    let bestMinY = 0;
    let bestMaxX = width - 1;
    let bestMaxY = height - 1;
    const queue: number[] = [];

    for (let seed = 0; seed < pixelCount; seed += 1) {
      if (!opaqueMask[seed] || visited[seed]) continue;

      componentQueue.length = 0;
      componentQueue.push(seed);
      visited[seed] = 1;
      let size = 0;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      for (let head = 0; head < componentQueue.length; head += 1) {
        const flat = componentQueue[head];
        const x = flat % width;
        const y = Math.floor(flat / width);
        size += 1;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;

        const left = x > 0 ? flat - 1 : -1;
        const right = x < width - 1 ? flat + 1 : -1;
        const up = y > 0 ? flat - width : -1;
        const down = y < height - 1 ? flat + width : -1;
        const neighbors = [left, right, up, down];
        neighbors.forEach((nextFlat) => {
          if (nextFlat < 0) return;
          if (!opaqueMask[nextFlat] || visited[nextFlat]) return;
          visited[nextFlat] = 1;
          componentQueue.push(nextFlat);
        });
      }

      if (size > bestSize && maxX >= minX && maxY >= minY) {
        bestSize = size;
        bestMinX = minX;
        bestMinY = minY;
        bestMaxX = maxX;
        bestMaxY = maxY;
      }
    }

    if (!bestSize) return dataUrl;

    const cropWidth = bestMaxX - bestMinX + 1;
    const cropHeight = bestMaxY - bestMinY + 1;
    if (!cropWidth || !cropHeight) return dataUrl;

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = cropWidth;
    croppedCanvas.height = cropHeight;
    const croppedCtx = croppedCanvas.getContext('2d');
    if (!croppedCtx) return trimmedGlobal.toDataURL('image/png');

    croppedCtx.drawImage(
      canvas,
      bestMinX,
      bestMinY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    const trimmedComponent = trimCanvas(croppedCanvas);
    if (!trimmedComponent.width || !trimmedComponent.height) {
      return trimmedGlobal.toDataURL('image/png');
    }

    const scoreFit = (w: number, h: number) => {
      const boxW = PRINT_PAGE_WIDTH_MM;
      const boxH = PRINT_PAGE_HEIGHT_MM;
      const scaleNoRotate = Math.min(boxW / w, boxH / h);
      const scaleRotate = Math.min(boxW / h, boxH / w);
      const areaNoRotate = w * scaleNoRotate * h * scaleNoRotate;
      const areaRotate = h * scaleRotate * w * scaleRotate;
      return Math.max(areaNoRotate, areaRotate) / (boxW * boxH);
    };

    const globalScore = scoreFit(trimmedGlobal.width, trimmedGlobal.height);
    const componentScore = scoreFit(trimmedComponent.width, trimmedComponent.height);

    const selected = componentScore > globalScore ? trimmedComponent : trimmedGlobal;
    return selected.toDataURL('image/png');
  };

  const focusDiscountEntries = useMemo(() => {
    if (!isSocioAmbientalScope) return [];
    return focuses
      .map((focus) => ({
        name: focus.name,
        value: focus.discount?.value ?? 0,
        reasons: focus.discount?.reasons ?? [],
      }))
      .filter((entry) => entry.value > 0);
  }, [focuses, isSocioAmbientalScope]);

  const handleDownloadResults = async () => {
    if (!results || !currentFulfillmentVersion) return;
    setIsDownloading(true);
    try {
      const response = powerBiLoaded
        ? { dataUrl: powerBiImage, node0DataUrl: powerBiNode0Image }
        : await dataSource.getSelfAssessmentPowerBiReportImage(currentFulfillmentVersion);

      if (!powerBiLoaded) {
        setPowerBiLoaded(true);
      }

      const dataUrl = response.dataUrl ?? null;
      const node0DataUrl = response.node0DataUrl ?? null;

      if (!dataUrl && !node0DataUrl) {
        if (powerBiImage || powerBiNode0Image) {
          setPowerBiImage(null);
          setPowerBiNode0Image(null);
          await new Promise((resolve) =>
            requestAnimationFrame(() => requestAnimationFrame(resolve)),
          );
        }
        window.print();
        return;
      }

      const [trimmedDataUrl, trimmedNode0DataUrl] = await Promise.all([
        dataUrl ? trimPowerBiImageDataUrl(dataUrl).catch(() => dataUrl) : Promise.resolve(null),
        node0DataUrl
          ? trimPowerBiImageDataUrl(node0DataUrl).catch(() => node0DataUrl)
          : Promise.resolve(null),
      ]);

      if (trimmedDataUrl !== powerBiImage) {
        setPowerBiImage(trimmedDataUrl);
      }
      if (trimmedNode0DataUrl !== powerBiNode0Image) {
        setPowerBiNode0Image(trimmedNode0DataUrl);
      }

      const urlsToLoad = [trimmedDataUrl, trimmedNode0DataUrl].filter(
        (url): url is string => Boolean(url),
      );
      const uniqueUrls = Array.from(new Set(urlsToLoad));

      const loadedImages = await Promise.all(
        uniqueUrls.map(
          (url) =>
            new Promise<{ url: string; width: number; height: number }>((resolve, reject) => {
              const img = new Image();
              img.onload = () =>
                resolve({
                  url,
                  width: img.naturalWidth || img.width,
                  height: img.naturalHeight || img.height,
                });
              img.onerror = () => reject(new Error('Power BI image failed to load'));
              img.src = url;
            }),
        ),
      );

      if (loadedImages.length) {
        setPowerBiImageSizes((prev) => {
          let changed = false;
          const next = { ...prev };
          loadedImages.forEach(({ url, width, height }) => {
            const current = prev[url];
            if (!current || current.width !== width || current.height !== height) {
              next[url] = { width, height };
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }

      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      window.print();
    } catch (error) {
      console.error('Error printing results view', error);
      runThunk(
        setSnackAlert({
          open: true,
          message: t('apiFeedback.selfAssessmentResults.powerBiExportError'),
          severity: 'error',
        }),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBack = () => {
    navigate('/ceo-validation');
  };

  const handleExit = () => {
    navigate('/');
  };

  if (resultsStepPaused) {
    return null;
  }

  return (
    <Box
      id="self-assessment-results"
      sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: { xs: 2, md: 4 } }}
    >
      <GlobalStyles
        styles={{
          '@page': {
            size: 'A4',
            margin: `${PRINT_PAGE_MARGIN_MM}mm`,
          },
          '.print-only': {
            display: 'none',
          },
          '@media print': {
            '*': {
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
            },
            body: {
              margin: 0,
              backgroundColor: '#ffffff',
            },
            '#self-assessment-results': {
              backgroundColor: '#ffffff',
              paddingTop: 0,
            },
            '#self-assessment-results > div': {
              margin: '0 !important',
              padding: '0 !important',
              maxWidth: 'none !important',
              width: '100% !important',
            },
            '#self-assessment-results > div > .MuiPaper-root': {
              borderRadius: '0 !important',
              boxShadow: 'none !important',
            },
            '.print-hide': {
              display: 'none !important',
            },
            '.print-avoid-break': {
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            },
            '.print-only': {
              display: 'block',
            },
            '.print-powerbi-page': {
              breakBefore: 'page',
              pageBreakBefore: 'always',
              breakAfter: 'page',
              pageBreakAfter: 'always',
              margin: '0 !important',
              padding: '0 !important',
              border: 'none !important',
              boxShadow: 'none !important',
              width: `${PRINT_PAGE_WIDTH_MM}mm`,
              height: `${PRINT_PAGE_HEIGHT_MM}mm`,
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              overflow: 'hidden',
              backgroundColor: '#ffffff',
            },
            '.print-powerbi-slot': {
              minHeight: 0,
              flex: '1 1 auto',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            },
            '.print-powerbi-rotator': {
              width: `${PRINT_PAGE_HEIGHT_MM}mm`,
              height: `${PRINT_PAGE_WIDTH_MM}mm`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'rotate(90deg)',
              transformOrigin: 'center center',
            },
            '.print-powerbi-rotator.print-powerbi-rotator--no-rotate': {
              width: '100%',
              height: '100%',
              transform: 'none',
            },
            '.print-powerbi-image': {
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center center',
              transform: 'none',
              transformOrigin: 'center center',
              display: 'block',
            },
            '.print-powerbi-image.print-powerbi-image--boost': {
              transform: 'scale(1.1)',
            },
          },
        }}
      />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1366px', mx: 'auto', width: '100%' }}>
        <Paper
          sx={{
            borderRadius: 1,
            boxShadow: 2,
            overflow: 'hidden',
            '@media print': { boxShadow: 'none' },
          }}
        >
          <Grid
            container
            justifyContent={{ xs: 'flex-start', md: 'space-between' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{
              px: { xs: 2, md: 3 },
              pt: 2,
              bgcolor: '#fafafa',
              borderBottom: '1px solid #A5B2BB',
              rowGap: { xs: 2, md: 0 },
            }}
          >
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Grid
                container
                spacing={2}
                sx={{
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  rowGap: { xs: 1.5, sm: 0 },
                }}
              >
                <Grid
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('common.annualPlan')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {yearLabel}
                  </Typography>
                </Grid>

                <Grid
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('newAnnualPlan.scope')} {scopeLabel}
                    {currentBusinessUnit
                      ? ` · ${t('newAnnualPlan.businessUnit')}: ${currentBusinessUnit}`
                      : ''}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {isSocioAmbientalScope
                      ? t('selfAssessmentResults.environmentScopeSummary')
                      : t('selfAssessmentResults.title')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              size={{ xs: 12, md: 'auto' }}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'center' },
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownload />}
                onClick={handleDownloadResults}
                disabled={isDownloading || !results}
                sx={{ minWidth: 180 }}
                className="print-hide"
              >
                {isDownloading ? t('common.loading') : t('selfAssessmentResults.printResults')}
              </Button>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={PlanAnualIcon}
                  alt={t('common.annualPlan')}
                  style={{ width: 70, height: 70 }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'white' }}>
            <Grid container spacing={2} sx={{ mb: 4, flexWrap: 'wrap' }} className="print-hide">
              {[1, 2, 3].map((step) => (
                <Grid size={stepGridSize} key={step}>
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      bgcolor: '#D8FFEC',
                      color: '#333',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #A5B2BB',
                      height: '100%',
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid size="auto">
                        <Grid
                          container
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            border: '1px solid #A5B2BB',
                            bgcolor: '#4caf50',
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Grid size="auto">
                            <Check />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size="auto">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('common.step')} {step}
                        </Typography>
                        <Typography variant="caption">
                          {step === 1
                            ? t('enterCompliance.enterComplianceData')
                            : step === 2
                              ? t('enterCompliance.maturityLevelEvaluation')
                              : t('enterCompliance.selfAssessmentAndSignature')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}

              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#00BCE4',
                    color: 'white',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                    height: '100%',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          color: '#00BCE4',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">4</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step')} 4
                      </Typography>
                      <Typography variant="caption">
                        {isSocioAmbientalScope
                          ? t('selfAssessmentResults.environmentScopeSummary')
                          : t('selfAssessmentResults.title')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>

            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                borderRadius: 2,
                border: '1px solid #A5B2BB',
                background: 'linear-gradient(135deg, #E3F2FD 0%, #F1F8FF 100%)',
                '@media print': { breakInside: 'auto', pageBreakInside: 'auto' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  rowGap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0B5563' }}>
                  {isSocioAmbientalScope
                    ? t('selfAssessmentResults.environmentScopeSummary')
                    : t('selfAssessmentResults.securityScopeSummary')}
                </Typography>
                {isSecurityScope && contributionChips ? (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Chip
                      label={`${enterpriseContributionLabel}: ${formatPercentage(contributionChips.globalIndex * 100, 0)}% - ${contributionChips.inTop80Global ? top80ContributionYes : top80ContributionNo}`}
                      size="small"
                      color={contributionChips.inTop80Global ? 'warning' : 'default'}
                    />
                    <Chip
                      label={`${businessLineContributionLabel}: ${formatPercentage(contributionChips.businessLineIndex * 100, 0)}% - ${contributionChips.inTop80BL ? top80ContributionYes : top80ContributionNo}`}
                      size="small"
                      color={contributionChips.inTop80BL ? 'warning' : 'default'}
                    />
                    <Chip
                      label={`IFCTP UEN: ${contributionChips.uenIfctp.toFixed(1)}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`IFCTP Línea: ${contributionChips.businessLineIfctp.toFixed(1)}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                ) : null}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#455a64',
                  mb: 2,
                }}
              >
                <InfoOutlined sx={{ fontSize: 18, color: '#607d8b' }} />
                <Typography variant="body2" sx={{ color: '#455a64' }}>
                  {t('selfAssessmentResults.activitiesCutoffNote', {
                    month: ytdCutoff.monthLabel,
                    year: ytdCutoff.year,
                  })}
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 2, alignItems: 'stretch' }}>
                <Grid size={{ xs: 12, md: isSocioAmbientalScope ? 6 : 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor: '#fff',
                      border: '1px solid #c5d9e4',
                      background: 'linear-gradient(135deg, #E3F2FD 0%, #F1F8FF 100%)',
                      height: '100%',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0B5563' }}>
                      {isSocioAmbientalScope
                        ? t('selfAssessmentResults.workPlan')
                        : t('selfAssessmentResults.overallAverage')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 900,
                          color: '#01579b',
                          lineHeight: 1.1,
                        }}
                      >
                        {formatPercentage(
                          isSocioAmbientalScope
                            ? (workPlanWithDiscounts ?? globalAdherence)
                            : overallAverage,
                          1,
                        )}
                        %
                      </Typography>
                      {isSecurityScope && overallWithoutAdjustments != null ? (
                        <LightTooltip
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', fontWeight: 800, color: '#0f172a' }}
                              >
                                {t('selfAssessmentResults.overallWithoutDiscounts')}:{' '}
                                {formatPercentage(overallWithoutAdjustments ?? 0, 1)}%
                              </Typography>
                              {contributionChips ? (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', fontWeight: 800, color: '#0f172a' }}
                                  >
                                    {t('selfAssessmentResults.appliedDiscounts')}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', color: '#0f172a' }}
                                  >
                                    {enterpriseDiscountLabel}:{' '}
                                    {formatSignedPercentage(contributionChips.discountGlobal, 1)}%
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', color: '#0f172a' }}
                                  >
                                    {businessLineDiscountLabel}:{' '}
                                    {formatSignedPercentage(contributionChips.discountBusiness, 1)}%
                                  </Typography>
                                </Box>
                              ) : null}
                            </Box>
                          }
                          arrow
                        >
                          <InfoOutlined sx={{ fontSize: 20, color: '#607d8b' }} />
                        </LightTooltip>
                      ) : null}
                    </Box>
                    {isSecurityScope && overallWithoutAdjustments != null ? (
                      <Typography variant="body2" sx={{ color: '#455a64', mt: 1 }}>
                        {t('selfAssessmentResults.overallWithoutDiscounts')}:{' '}
                        {formatPercentage(overallWithoutAdjustments ?? 0, 1)}%
                      </Typography>
                    ) : null}
                    {isSocioAmbientalScope ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                        <Typography variant="body2" sx={{ color: '#455a64', fontWeight: 700 }}>
                          {t('selfAssessmentResults.workPlanWithoutDiscounts')}:{' '}
                          {formatPercentage(globalAdherence, 1)}%
                        </Typography>
                        {focusDiscountEntries.length > 0 ? (
                          <LightTooltip
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    fontWeight: 800,
                                    color: '#0f172a',
                                    mb: 0.5,
                                  }}
                                >
                                  {t('selfAssessmentResults.appliedDiscounts')}
                                </Typography>
                                {focusDiscountEntries.map((d) => (
                                  <Box key={d.name} sx={{ mb: 0.75 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ display: 'block', fontWeight: 800, color: '#0f172a' }}
                                    >
                                      {formatSignedPercentage(d.value, 1)}% · {d.name}
                                    </Typography>
                                    {d.reasons.map((r, idx) => (
                                      <Typography
                                        key={idx}
                                        variant="caption"
                                        sx={{ display: 'block', color: '#0f172a' }}
                                      >
                                        {formatSignedPercentage(r.value, 1)}% · {r.label}
                                      </Typography>
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            }
                            arrow
                          >
                            <InfoOutlined sx={{ fontSize: 18, color: '#607d8b' }} />
                          </LightTooltip>
                        ) : null}
                      </Box>
                    ) : null}
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: isSocioAmbientalScope ? 6 : 8 }}>
                  <Grid container spacing={2}>
                    {visibleSummaryCards.map((card) => (
                      <Grid
                        key={card.key || card.title}
                        size={{
                          xs: 12,
                          sm: 6,
                          md: isSocioAmbientalScope
                            ? visibleSummaryCards.length === 1
                              ? 12
                              : 6
                            : 4,
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: '#fff',
                            border: '1px solid #d7e3eb',
                            height: '100%',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: '#0B5563' }}
                          >
                            {card.title}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 800,
                                  color: '#01579b',
                                }}
                              >
                                {formatPercentage(card.value, 1)}%
                              </Typography>
                            </Box>
                            {card.key === 'managementFocus' &&
                            isSecurityScope &&
                            auditPercentage != null ? (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#4b5563', fontWeight: 700, lineHeight: 1.2 }}
                                >
                                  {t('selfAssessmentResults.auditLabel')}:{' '}
                                  {formatPercentage(auditPercentage, 1)}%
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#6b7280', fontWeight: 600, lineHeight: 1.2 }}
                                >
                                  {t('selfAssessmentResults.valueWithoutWeighting', {
                                    source: t('selfAssessmentResults.weightingSource.audit'),
                                  })}
                                  :{' '}
                                  {managementFocusOriginal != null
                                    ? `${formatPercentage(managementFocusOriginal, 1)}%`
                                    : '—'}
                                </Typography>
                              </Box>
                            ) : null}
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #c5d9e4',
                  bgcolor: '#f7fbff',
                  '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
                }}
              >
                <Grid container spacing={2} alignItems="stretch">
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid #c5d9e4',
                        bgcolor: '#ffffff',
                        height: '100%',
                      }}
                    >
                      {showCulture ? (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800, color: '#0B5563' }}
                          >
                            {t('selfAssessmentResults.cultureLevel')}
                          </Typography>
                          {isSecurityScope ? (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <Typography
                                  variant="h4"
                                  sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1.1 }}
                                >
                                  {cultureLevelLabel}
                                </Typography>
                                <LightTooltip title={previousLevelTooltip} arrow>
                                  <InfoOutlined sx={{ fontSize: 20, color: '#607d8b' }} />
                                </LightTooltip>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  color: '#4b5563',
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                }}
                              >
                                {t('selfAssessmentResults.maturityLevel')}:{' '}
                                {maturityLevelLabel || '—'}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.5,
                                  color: '#6b7280',
                                  fontWeight: 600,
                                  lineHeight: 1.2,
                                }}
                              >
                                {t('selfAssessmentResults.previousMaturityLevel')}:{' '}
                                {previousLevelLabel}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography
                                variant="h4"
                                sx={{
                                  mt: 1,
                                  fontWeight: 900,
                                  color: '#0f172a',
                                  lineHeight: 1.1,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box component="span">{cultureLevelLabel}</Box>
                                  {hasLegalNonComplianceDegradation && (
                                    <LightTooltip
                                      title={t(
                                        'selfAssessmentResults.legalNonComplianceDegradationTooltip',
                                      )}
                                      arrow
                                    >
                                      <InfoOutlined
                                        sx={{ fontSize: 20, color: '#607d8b', cursor: 'pointer' }}
                                      />
                                    </LightTooltip>
                                  )}
                                </Box>
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  color: '#4b5563',
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                }}
                              >
                                {t('selfAssessmentResults.maturityLevel')}:{' '}
                                {maturityLevelLabel || '—'}
                              </Typography>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800, color: '#0B5563' }}
                          >
                            {t('selfAssessmentResults.maturityLevel')}
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 900, color: '#0f172a', mt: 1, lineHeight: 1.1 }}
                          >
                            {maturityLevelLabel || '—'}
                          </Typography>
                        </>
                      )}
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={1}>
                      {[1, 2, 3, 4].map((lvl) => (
                        <Grid key={lvl} size={{ xs: 6, sm: 3 }}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.25,
                              borderRadius: 1.5,
                              border: '1px solid #e0e7ee',
                              bgcolor: '#ffffff',
                              textAlign: 'center',
                              height: '100%',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 700, color: '#01579b' }}
                            >
                              {t('selfAssessmentResults.maturityLevelShort', { level: lvl })}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                              {formatPercentage(maturityProgress[lvl] ?? 0, 0)}%
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Paper>

            {hasPowerBiImages ? (
              <>
                {printPowerBiImages.map((imageSrc, index) => {
                  const imageSize = powerBiImageSizes[imageSrc];
                  const shouldRotate = imageSize ? imageSize.width >= imageSize.height : true;
                  const estimateFillRatio = () => {
                    if (!imageSize || !imageSize.width || !imageSize.height) return 0;
                    const boxWidth = shouldRotate ? PRINT_PAGE_HEIGHT_MM : PRINT_PAGE_WIDTH_MM;
                    const boxHeight = shouldRotate ? PRINT_PAGE_WIDTH_MM : PRINT_PAGE_HEIGHT_MM;
                    const fitScale = Math.min(
                      boxWidth / imageSize.width,
                      boxHeight / imageSize.height,
                    );
                    const renderedWidth = imageSize.width * fitScale;
                    const renderedHeight = imageSize.height * fitScale;
                    return (renderedWidth * renderedHeight) / (boxWidth * boxHeight);
                  };
                  const shouldBoostScale = estimateFillRatio() < 0.75;
                  return (
                    <Paper
                      key={`print-powerbi-${index}`}
                      className="print-only print-powerbi-page"
                      elevation={0}
                      sx={{
                        mb: 0,
                        borderRadius: 0,
                      }}
                    >
                      <Box className="print-powerbi-slot">
                        <Box
                          className={`print-powerbi-rotator${
                            shouldRotate ? '' : ' print-powerbi-rotator--no-rotate'
                          }`}
                        >
                          <img
                            src={imageSrc}
                            alt={t('selfAssessmentResults.powerBiImageAlt')}
                            className={`print-powerbi-image${
                              shouldBoostScale ? ' print-powerbi-image--boost' : ''
                            }`}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </>
            ) : null}

            <Paper
              elevation={0}
              sx={{
                mb: 4,
                borderRadius: 2,
                p: 3,
                background: 'linear-gradient(135deg, #E0F7FA 0%, #E8F5FF 100%)',
                border: '1px solid #A5B2BB',
                '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0B5563' }}>
                    {t('selfAssessmentResults.workPlan')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1b4b59', mt: 1 }}>
                    {t('selfAssessmentResults.workPlanWeightedTitle')}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      color: '#0f172a',
                      mt: 2,
                      background: 'linear-gradient(90deg, #0288d1, #00bcd4)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {formatPercentage(workPlanWithDiscounts ?? globalAdherence, 1)}%
                  </Typography>
                  {isSocioAmbientalScope ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                      <Typography variant="body2" sx={{ color: '#1b4b59', fontWeight: 700 }}>
                        {t('selfAssessmentResults.workPlanWithoutDiscounts')}:{' '}
                        {formatPercentage(globalAdherence, 1)}%
                      </Typography>
                      {focusDiscountEntries.length > 0 ? (
                        <LightTooltip
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  fontWeight: 800,
                                  color: '#0f172a',
                                  mb: 0.5,
                                }}
                              >
                                {t('selfAssessmentResults.appliedDiscounts')}
                              </Typography>
                              {focusDiscountEntries.map((d) => (
                                <Box key={d.name} sx={{ mb: 0.75 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', fontWeight: 800, color: '#0f172a' }}
                                  >
                                    {formatSignedPercentage(d.value, 1)}% · {d.name}
                                  </Typography>
                                  {d.reasons.map((r, idx) => (
                                    <Typography
                                      key={idx}
                                      variant="caption"
                                      sx={{ display: 'block', color: '#0f172a' }}
                                    >
                                      {formatSignedPercentage(r.value, 1)}% · {r.label}
                                    </Typography>
                                  ))}
                                </Box>
                              ))}
                            </Box>
                          }
                          arrow
                        >
                          <InfoOutlined sx={{ fontSize: 18, color: '#607d8b' }} />
                        </LightTooltip>
                      ) : null}
                    </Box>
                  ) : null}
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    {focuses.map((foco) => {
                      const pct = foco.fulfillmentPct ?? 0;
                      const originalPct = foco.fulfillmentPctOriginal ?? null;
                      const discount = foco.discount;
                      const previousPct =
                        originalPct != null ? Math.max(0, originalPct - (discount?.value ?? 0)) : null;
                      const hasWeightedComparison =
                        previousPct != null &&
                        Boolean(foco.weightingSource) &&
                        Math.abs((pct ?? 0) - previousPct) > 0.0001;
                      return (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={foco.id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #CFE7EF',
                              bgcolor: '#fff',
                              height: '100%',
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, color: '#0B5563', mb: 0.5 }}
                            >
                              {foco.name}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#01579b' }}>
                              {formatPercentage(pct, 1)}%
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#607d8b' }}>
                              {t('ceoValidation.focusFulfillmentPct')}
                            </Typography>
                            {isSocioAmbientalScope && originalPct != null ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mt: 0.75,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#1b4b59', fontWeight: 700 }}
                                >
                                  {t('selfAssessmentResults.workPlanWithoutDiscounts')}:{' '}
                                  {formatPercentage(originalPct, 1)}%
                                </Typography>
                                {hasWeightedComparison ? (
                                  <Typography
                                    variant="caption"
                                    sx={{ display: 'block', color: '#1b4b59', fontWeight: 700 }}
                                  >
                                    {t('selfAssessmentResults.valueWithoutWeighting', {
                                      source: t(
                                        `selfAssessmentResults.weightingSource.${foco.weightingSource}`,
                                      ),
                                    })}
                                    :{' '}
                                    {formatPercentage(previousPct ?? 0, 1)}%
                                  </Typography>
                                ) : null}
                                {discount?.value ? (
                                  <LightTooltip
                                    title={
                                      <Box sx={{ p: 0.5 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: 'block',
                                            fontWeight: 800,
                                            color: '#0f172a',
                                            mb: 0.5,
                                          }}
                                        >
                                          {t('selfAssessmentResults.appliedDiscounts')}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: 'block',
                                            fontWeight: 800,
                                            color: '#0f172a',
                                            mb: discount.reasons.length ? 0.5 : 0,
                                          }}
                                        >
                                          {formatSignedPercentage(discount.value, 1)}%
                                        </Typography>
                                        {discount.reasons.map((r, idx) => (
                                          <Typography
                                            key={idx}
                                            variant="caption"
                                            sx={{ display: 'block', color: '#0f172a' }}
                                          >
                                            {formatSignedPercentage(r.value, 1)}% · {r.label}
                                          </Typography>
                                        ))}
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <InfoOutlined sx={{ fontSize: 16, color: '#607d8b' }} />
                                  </LightTooltip>
                                ) : null}
                              </Box>
                            ) : null}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            {focusAdherenceData.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: 2,
                  p: 3,
                  border: '1px solid #A5B2BB',
                  background: 'linear-gradient(135deg, #fefefe 0%, #f2f8fb 100%)',
                  '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexWrap: 'wrap',
                    rowGap: 1,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0B5563' }}>
                      {t('selfAssessmentResults.focusAdherenceTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#455a64' }}>
                      {t('selfAssessmentResults.focusAdherenceDescription')}
                    </Typography>
                  </Box>
                  {totalFocusAdherenceWeighted != null && (
                    <Paper
                      elevation={0}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 999,
                        border: '1px solid #d7e3eb',
                        bgcolor: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#01579b' }}>
                        {t('selfAssessmentResults.totalWeightedAdherence')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                        {formatPercentage(totalFocusAdherenceWeighted, 1)}%
                      </Typography>
                    </Paper>
                  )}
                </Box>
                <Grid container spacing={2}>
                  {focusAdherenceData.map((focus) => {
                    const hasRequired = (focus.totalRequired ?? 0) > 0;
                    const unmetLabel = hasRequired
                      ? t('selfAssessmentResults.unmetRequirementsLabel')
                      : t('selfAssessmentResults.noRequiredRequirements');
                    const adherenceValue = hasRequired
                      ? `${formatPercentage(focus.adherencePct ?? 0, 1)}%`
                      : t('selfAssessmentResults.noRequiredRequirements');
                    const satisfiedRequired = hasRequired
                      ? Math.max(
                          Math.min(focus.satisfiedRequired ?? 0, focus.totalRequired ?? 0),
                          0,
                        )
                      : 0;
                    const weightedLabel =
                      focus.weightedContribution != null
                        ? `${formatPercentage(focus.weightedContribution, 1)}%`
                        : t('selfAssessmentResults.noData');
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={focus.id}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid #d7e3eb',
                            bgcolor: '#ffffff',
                            height: '100%',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: '#0B5563', mb: 1 }}
                          >
                            {focus.name}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'stretch',
                              justifyContent: 'space-between',
                              gap: 1.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Box sx={{ minWidth: 180, flex: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ color: '#607d8b', fontWeight: 700 }}
                              >
                                {t('selfAssessmentResults.requirementAdherenceLabel')}
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ fontWeight: 800, color: '#01579b', mt: 0.5 }}
                              >
                                {adherenceValue}
                              </Typography>
                              {hasRequired ? (
                                <Typography variant="caption" sx={{ color: '#607d8b' }}>
                                  {t('selfAssessmentResults.requirementsSummary', {
                                    satisfied: satisfiedRequired,
                                    total: focus.totalRequired ?? 0,
                                  })}
                                </Typography>
                              ) : null}
                            </Box>
                            <Box
                              sx={{
                                minWidth: 150,
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                border: '1px solid #ffe0b2',
                                bgcolor: '#fff7ed',
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                gap: 0.25,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: '#e65100', fontWeight: 700 }}
                              >
                                {unmetLabel}
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ fontWeight: 800, color: hasRequired ? '#bf360c' : '#607d8b' }}
                              >
                                {hasRequired ? focus.notSatisfied : '-'}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                minWidth: 150,
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                border: '1px solid #d7e3eb',
                                bgcolor: '#f7fbff',
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                gap: 0.25,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: '#0B5563', fontWeight: 700 }}
                              >
                                {t('selfAssessmentResults.weightedFocusContribution')}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: '#01579b' }}>
                                {weightedLabel}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#607d8b' }}>
                                {t('selfAssessmentResults.focusWeight', { weight: focus.weight })}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            )}

            {leadershipActivities && leadershipActivities.details.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  p: 3,
                  mb: 4,
                  border: '1px solid #A5B2BB',
                  background: 'linear-gradient(135deg, #fefefe 0%, #f4f9ff 100%)',
                  '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0B5563', mb: 2 }}>
                  {t('selfAssessmentResults.leadershipActivitiesTitle')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {t('selfAssessmentResults.leadershipActivity')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {t('selfAssessmentResults.leadershipCommitted')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {t('selfAssessmentResults.leadershipPerformed')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {t('selfAssessmentResults.leadershipPercentage')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leadershipActivities.details.map((item) => (
                        <TableRow key={item.key}>
                          <TableCell>{t(`selfAssessmentResults.${item.key}`)}</TableCell>
                          <TableCell align="right">
                            {item.committed.toLocaleString('es-CL')}
                          </TableCell>
                          <TableCell align="right">
                            {item.performed.toLocaleString('es-CL')}
                          </TableCell>
                          <TableCell align="right">
                            {item.percentage == null
                              ? 'N/A'
                              : `${formatPercentage(item.percentage, 1)}%`}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t('selfAssessmentResults.leadershipActivitiesTotal')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {leadershipActivities.totals.committed.toLocaleString('es-CL')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {leadershipActivities.totals.performed.toLocaleString('es-CL')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {leadershipActivities.totals.percentage == null
                            ? 'N/A'
                            : `${formatPercentage(leadershipActivities.totals.percentage, 1)}%`}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {Object.keys(processedChecklistData).length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  p: 3,
                  mb: 4,
                  border: '1px solid #A5B2BB',
                  background: 'linear-gradient(135deg, #fefefe 0%, #f4f9ff 100%)',
                  '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0B5563', mb: 2 }}>
                  {scopeLabel} - {t('ceoValidation.securityManagementSelfAssessment')}
                </Typography>
                <Grid container spacing={2}>
                  {Object.values(processedChecklistData).map((category) => (
                    <Grid size={{ xs: 12, md: 6 }} key={category.categoryName}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid #d7e3eb',
                          bgcolor: '#ffffff',
                          height: '100%',
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#263238' }}>
                          {category.categoryName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#607d8b', mb: 1 }}>
                          {category.checklists.length} checklist(s) · {t('common.average')}:{' '}
                          {category.avgScore}%
                        </Typography>
                        <Grid container spacing={1}>
                          {category.checklists.map((chk) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={chk.ChecklistId}>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 1.5,
                                  border: '1px solid #e0e7ee',
                                  bgcolor: '#f7fbff',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={chk.Name}
                                >
                                  {chk.Name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#607d8b' }}>
                                  {chk.progress}%
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            <Box className="print-hide">
              <Navigation
                onNext={handleExit}
                nextTitle={t('selfAssessmentResults.finish')}
                disableNext={false}
                onBack={handleBack}
                backTitle={t('common.backToStep3')}
                onExit={handleExit}
                exitTitle={t('common.exit')}
                isAnyLoading={isLoading}
                readOnlyMode
              />
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default SelfAssessmentResults;
